import { IconicDocumentShield, IconicNewWindow, IconicWarningTriangle } from '@insify/design-system/iconic-icons';
import { Spinner } from '@insify/ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetCoiLink } from '../../../api/queries/useGetCoiLink';
import { GTMEvent, useTagManager } from '../../../hooks/useTagManager';
import { useCountrySpecificContent } from '../../../utils/localize';
import { ActionGroup } from '../../ActionGroup';
import { ProductCard } from '../../Products';
import { Tabs } from '../../Tabs';
import styles from '../styles.module.css';

import type { Bundle, Coverage } from '../../../types/Policy';

interface Props {
  bundle: Bundle;
}
const ProductOverviewTab: React.FC<Props> = ({ bundle }) => {
  const { t } = useTranslation('common');
  const { landing_page_report_damage } = useCountrySpecificContent();

  const onFileClaimClick = () => {
    window.open(landing_page_report_damage, '_blank');
  };

  // TODO: this can be handled better, to determine after bundle type overhaul
  const policyDownloadButton = useMemo(() => {
    let activeCoverage;
    if (bundle.type === 'company') {
      activeCoverage =
        bundle.coverages.length === 0
          ? null
          : bundle.coverages.filter(
              (coverage) => coverage.title === 'coverage_professionalLiability' && coverage.status !== 'cancelled',
            )[0];

      return activeCoverage ? <CompanyCOIDownloadItem coverage={activeCoverage} /> : null;
    }

    activeCoverage = bundle.coverages[0];
    const hasDocument = activeCoverage?.documents?.at(0)?.url;
    return activeCoverage && hasDocument ? <PersonalPolicyDownloadItem coverage={activeCoverage} /> : null;
  }, [bundle]);

  return (
    <Tabs.Panel id='overview'>
      <div className={styles.grid}>
        <ProductCard
          bundle={bundle}
          additionalContent={bundle.coverages.length > 0 && bundle.coverages[0].policyNumber}
        />

        <div style={{ gridColumn: 'span 2', gridRow: 'span 1' }}></div>

        <div className={styles.section}>
          <h2>{t('quick_actions')}</h2>
          <ActionGroup.Container density='compact'>
            {policyDownloadButton}

            <ActionGroup.Item onClick={onFileClaimClick} trackingId='file-claim'>
              <ActionGroup.Title isOnlyChild icon={<IconicWarningTriangle />}>
                {t('quick_actions.report_a_claim')}
              </ActionGroup.Title>
            </ActionGroup.Item>
          </ActionGroup.Container>
        </div>
      </div>
    </Tabs.Panel>
  );
};

interface CertificateDownloaderProps {
  coverage: Coverage;
}

const CompanyCOIDownloadItem: React.FC<CertificateDownloaderProps> = ({ coverage }) => {
  const { t } = useTranslation('common');
  const { mutateAsync: getCoiLink, isPending } = useGetCoiLink();
  const { sendEvent } = useTagManager();

  const handleClick = async () => {
    const pdf = await getCoiLink({ coverage: coverage.title, policyUuid: coverage.policyId });
    sendEvent(GTMEvent.COI, { s3COILink: 'true' });
    window.open(pdf, '_blank') || window.location.assign(pdf);
  };

  return (
    <ActionGroup.Item disabled={isPending} actionIcon={<IconicNewWindow />} onClick={handleClick} trackingId='view-coi'>
      <ActionGroup.Title isOnlyChild icon={isPending ? <Spinner /> : <IconicDocumentShield />}>
        {t('quick_actions.view_coi')}
      </ActionGroup.Title>
    </ActionGroup.Item>
  );
};

const PersonalPolicyDownloadItem: React.FC<CertificateDownloaderProps> = ({ coverage }) => {
  const { t } = useTranslation('common');
  const onViewPolicyClick = () => {
    window.open(coverage.documents && coverage.documents[0].url, '_blank');
  };

  return (
    <ActionGroup.Item actionIcon={<IconicNewWindow />} onClick={onViewPolicyClick} trackingId='view-personal-policy'>
      <ActionGroup.Title isOnlyChild icon={<IconicDocumentShield />}>
        {t('quick_actions.view_policy')}
      </ActionGroup.Title>
    </ActionGroup.Item>
  );
};

export { ProductOverviewTab };
